import {createAndUpdateSecret, createRepo, deleteRepo, getRepos} from "../../BackendFacade/apiCalls";
import {useCallback, useEffect, useMemo, useState} from "react";
import React from "react";
import {useMain} from "../../../../Utils/SessionControls/mainContext";
import OdxModal from "../../../../OdxComponents/NativeOdx/Modal/Odx-Modal";
import OdxInputField from "../../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";
import {animatedComponents, IRepo, ISecret, ITeam} from "../../Interfaces/IGithub";
import {HubPathRouting} from "../../../../HubFramework/pathBuilder";
import Select from 'react-select';
import OdxSelect from "../../../../OdxComponents/NativeOdx/FormControls/Odx-Select";
import {colorStyles, slugStyle} from "../Styles/styles";

interface Props {
    repo?: IRepo | null;
    costCenter?: string;
    isOpen: boolean;
    onClose: () => void;
    onSaved: () => Promise<void> | void;
    org: string;
    slug: string;
    allSecrets: ISecret[];
    allTeams: ITeam[];
    id?: string;
}

const VisibilityOptions = [
    {value: 'internal', label: 'Internal'},
    {value: 'private', label: 'Private'}
]

const repoSuffixPattern = /^[A-Za-z0-9_-]{3,32}$/;

const RepoSidesheet: React.FC<Props> = ({repo, costCenter, isOpen, onClose, onSaved, org, slug, allSecrets, allTeams , id}) => {
    const creating = !repo;
    const getEditableName = useCallback((name: string) => (name && name.startsWith(`${slug}-`)) ? name.slice(`${slug}-`.length) : '', [slug]);
    const [editableName, setEditableName] = useState('');
    const [visibility, setVisibility] = useState<'private' | 'internal'>('internal');
    const [deleting, setDeleting] = useState(false);
    const [selectedSecrets, setSelectedSecrets] = useState<ISecret[]>([]);
    const [selectedTeams, setSelectedTeams] = useState<ITeam[]>([]);
    const [nameError, setNameError] = useState<{ isActive: boolean, text?: string }>({isActive: false});
    const [initialSecrets, setInitialSecrets] = useState<ISecret[]>([]);
    const [saving, setSaving] = useState(false);

    const [enableSelectAnimations, setEnableSelectAnimations] = useState(false);
    const {setLoading, createToast} = useMain();

    useEffect(() => {
        if (!isOpen) return;
        setEnableSelectAnimations(false);
        const timer = setTimeout(() => setEnableSelectAnimations(true), 100);
        return () => clearTimeout(timer);
    }, [isOpen, repo]);

    useEffect(() => {
        setEditableName(getEditableName(repo?.name ?? ''));
        setVisibility('internal');
        setNameError({isActive: false});
        if (repo && allSecrets.length > 0) {
            const linkedSecrets = allSecrets.filter(secret =>
                secret.selectedRepos?.some(r => r.id === repo.id)
            );
            setSelectedSecrets(linkedSecrets);
            setInitialSecrets(linkedSecrets);
        } else {
            setSelectedSecrets([]);
            setInitialSecrets([]);
        }
        setSelectedTeams([]);
    }, [repo, isOpen, allSecrets, slug, getEditableName]);

    const secretOptions = useMemo(() => allSecrets.map(secret => ({
        value: secret.name, label: secret.name
    })), [allSecrets]);
    const teamOptions = useMemo(() => allTeams.map(team => ({
        value: team.name, label: team.name
    })), [allTeams]);
    const selectedSecretValues = useMemo(() => selectedSecrets.map(secret => ({
        value: secret.name, label: secret.name
    })), [selectedSecrets]);
    const selectedTeamValue = useMemo(() => {
        if (selectedTeams.length === 0) {
            return null;
        }
        const team = selectedTeams[0];
        return {value: team.name, label: team.name};
    }, [selectedTeams]);

    const handleSecretChange = useCallback((selected) => {
        setSelectedSecrets(selected ? selected.map(
            (opt: { value: string; label: string; }) => allSecrets.find(secret => secret.name === opt.value) || {name: opt.label, updated_at: ""}
        ) : []);
    }, [allSecrets]);

    const handleTeamChange = useCallback((selected) => {
        if (!selected) {
            setSelectedTeams([]);
            return;
        }

        const resolved = Array.isArray(selected) ? selected[0] : selected;
        if (resolved && typeof resolved === 'object' && 'value' in resolved) {
            const option = resolved as { value: string; label: string };
            const match = allTeams.find(team => team.name === option.value);
            if (match) {
                setSelectedTeams([match]);
                return;
            }
            const fallbackName = option.label || option.value;
            const fallbackSlug = option.value || option.label;
            setSelectedTeams([{name: fallbackName, slug: fallbackSlug}]);
            return;
        }

        if (typeof resolved === 'string' && resolved.length > 0) {
            const match = allTeams.find(team => team.name === resolved);
            if (match) {
                setSelectedTeams([match]);
            } else {
                setSelectedTeams([{name: resolved, slug: resolved}]);
            }
        }
    }, [allTeams]);


    const validateName = useCallback((candidate?: string) => {
        const value = candidate !== undefined ? candidate : editableName;
        if (!value.trim()) {
            setNameError({isActive: true, text: `Please provide a name after "${slug}-"`});
            return false;
        }
        const suffix = value.trim();
        if (!repoSuffixPattern.test(suffix)) {
            setNameError({
                isActive: true,
                text: 'Repository name must be 3-32 characters using letters, numbers, "-" or "_".'
            });
            return false;
        }
        setNameError({isActive: false});
        return true;
    }, [editableName, slug]);

    const updateSecret = useCallback(async (secret: ISecret, shouldLink: boolean) => {
        const currentRepos = secret.selectedRepos || [];
        let repoIDs: string[];
        if (!repo) return true;
        if (shouldLink) {
            if (!currentRepos.some(r => r.id === repo.id)) {
                repoIDs = [...currentRepos.map(r => r.id.toString()), repo.id.toString()];
            } else return true;
        } else {
            repoIDs = currentRepos.filter(r => r.id !== repo.id).map(r => r.id.toString());
        }
        try {
            await createAndUpdateSecret(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id, {
                secretName: secret.name, secretValue: '', repoIDs
            });
            return true;
        } catch (e) {
            createToast({
                message: `Failed to ${shouldLink ? 'link' : 'unlink'} secret ${secret.name}: ${e instanceof Error ? e.message : String(e)}`,
                variant: "warning",
                title: "Warning"
            });
            return false;
        }
    }, [repo, createToast]);

    const saveCreate = useCallback(async () => {
        if (!validateName()) return;
        setSaving(true);
        setLoading(true);
        try {
            if (selectedTeams.length === 0) {
                setSaving(false);
                setLoading(false);
                createToast({message: 'Please select a team to link to the repository.', variant: 'danger', title: 'Team required'});
                return;
            }
            const finalName = `${slug}-${editableName.trim()}`;
            const message = await createRepo(
                HubPathRouting.currentProject.id,
                HubPathRouting.currentModule.id,
                {costCenter, repoName: finalName, visibility, teamSlug: selectedTeams[0].slug}
            );
            if (!message.success) {
                createToast({
                    message: message.data.errorMessage || "Failed to create repository.",
                    variant: "danger",
                    title: "Error"
                });
                return;
            }
            const repoList = await getRepos();
            const newRepo = repoList.find((r: { name: string; }) => r.name === finalName);
            if (newRepo && selectedSecrets.length > 0) {
                for (const secret of selectedSecrets) {
                    const currentRepos = secret.selectedRepos || [];
                    const repoIDs = [...currentRepos.map(r => r.id.toString()), newRepo.id.toString()];
                    await createAndUpdateSecret(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id, {
                        secretName: secret.name,
                        secretValue: '',
                        repoIDs
                    });
                }
            }
            createToast({message: "Repository created", variant: "success", title: "Success"});
            onClose();
            await onSaved();
        } catch (e) {
            createToast({message: e instanceof Error ? e.message : String(e), variant: "danger", title: "Error"});
        } finally {
            setSaving(false);
            setLoading(false);
        }
    }, [validateName, editableName, org, slug, costCenter, visibility, selectedSecrets, onClose, onSaved, setLoading, createToast, selectedTeams]);

    const saveEdit = useCallback(async () => {
        if (!repo) return;
        setSaving(true);
        setLoading(true);
        try {
            const secretsToAdd = selectedSecrets.filter(s => !initialSecrets.some(o => o.name === s.name));
            const secretsToRemove = initialSecrets.filter(o => !selectedSecrets.some(s => s.name === o.name));
            for (const s of secretsToAdd) await updateSecret(s, true);
            for (const s of secretsToRemove) await updateSecret(s, false);
            createToast({message: "Repository updated", variant: "success", title: "Success"});
            onClose();
            await onSaved();
        } catch (e) {
            createToast({message: e instanceof Error ? e.message : String(e), variant: "danger", title: "Error"});
        } finally {
            setSaving(false);
            setLoading(false); // ALWAYS clear spinner
        }
    }, [repo, selectedSecrets, initialSecrets, updateSecret, onClose, onSaved, setLoading, createToast]);

    const doDelete = useCallback(async () => {
        if (!repo) return;
        if (!window.confirm(`Delete repository "${repo.name}"? This cannot be undone.`)) return;
        setDeleting(true);
        setLoading(true);
        try {
            await deleteRepo(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id, repo.name);
            createToast({message: "Repository deleted", variant: "success", title: "Deleted"});
            onClose();
            await onSaved();
        } catch (e) {
            createToast({message: e instanceof Error ? e.message : String(e), variant: "danger", title: "Error"});
        } finally {
            setDeleting(false);
            setLoading(false); // ALWAYS clear spinner
        }
    }, [repo, setLoading, createToast, onClose, onSaved]);

    const footer = useMemo(() => {
        if (creating) {
            return [
                <odx-button
                    id="repo-sidesheet-cancel-btn"
                    key="cancel"
                    variant="ghost"
                    type="button"
                    onClick={onClose}
                >
                    Cancel
                </odx-button>,
                <odx-button
                    id="repo-sidesheet-save-btn"
                    key="save"
                    variant="primary"
                    type="button"
                    onClick={saveCreate}
                    disabled={nameError.isActive || !editableName.trim() || selectedTeams.length === 0 || saving ? true : undefined}
                >
                    Save
                </odx-button>
            ];
        }
        return [
            <odx-button
                id="repo-sidesheet-delete-btn"
                key="delete"
                variant="danger"
                type="button"
                onClick={doDelete}
                disabled={deleting || saving ? true : undefined}
            >
                Delete
            </odx-button>,
            <odx-button
                id="repo-sidesheet-cancel-btn"
                key="cancel"
                variant="ghost"
                type="button"
                onClick={onClose}
                disabled={saving ? true : undefined}
            >
                Cancel
            </odx-button>,
            <odx-button
                id="repo-sidesheet-save-btn"
                key="save"
                variant="primary"
                type="button"
                onClick={saveEdit}
                disabled={saving ? true : undefined}
            >
                Save
            </odx-button>
        ];
    }, [creating, doDelete, deleting, onClose, saveEdit, saveCreate, nameError, editableName, saving]);

    return (
        <OdxModal
            size="small"
            variant="sidesheet"
            title={creating ? 'Add repository' : `Edit ${repo?.name}`}
            isOpen={isOpen}
            onClose={onClose}
            footerButtons={footer}
            id={id ? `${id}-modal${repo ? '-' + repo.id : ''}` : `repo-sidesheet-modal${repo ? '-' + repo?.id : ''}`}
        >
            <div data-odxLayout="vertical" id={id ? `${id}-vertical-layout` : "repo-sidesheet-vertical-layout"}>
                <div style={{display: 'flex', alignItems: 'center', gap: 0, marginBottom: creating ? undefined : '1em'}}>
                    <span style={slugStyle} id={id ? `${id}-slug` : "repo-sidesheet-slug"}>
                        {slug}-
                    </span>
                    <OdxInputField
                        id={id ? `${id}-name-input` : "repo-name-input"}
                        label="Name"
                        value={editableName}
                        onChange={e => {
                            const val = e.target.value.replace(/^[-\s]+/, '');
                            setEditableName(val);
                            validateName(val);
                        }}
                        isRequired={true}
                        error={nameError.isActive ? {isActive: true, text: nameError.text} : undefined}
                        className="flex-grow-1"
                        style={{borderRadius: '0 4px 4px 0', borderLeft: 'none'}}
                        isDisabled={!creating}
                    />
                </div>
                {creating && (
                    <>
                        <OdxSelect
                            label="Visibility"
                            id={id ? `${id}-visibility-select` : "visibility-select"}
                            placeholder="Select visibility"
                            value={VisibilityOptions.filter(opt => opt.value === visibility)[0].label}
                            onChange={e => {
                                if (typeof e === 'string') {
                                    setVisibility(e as 'private' | 'internal');
                                } else if (e && typeof e === 'object' && 'target' in e && typeof (e as any).target.value === 'string') {
                                    const value = (e as any).target.value;
                                    setVisibility(value === "private" ? "private" : "internal");
                                }
                            }}
                            isRequired={true}
                            options={VisibilityOptions}
                        />
                        <br/>
                    </>,
                        <div className="form-group mt-4" id={id ? `${id}-linked-teams-group` : "repo-linked-teams-group"}>
                            <label
                                id={id ? `${id}-linked-teams-label` : "repo-linked-teams-label"}>{creating ? "Link Teams to Repository" : "Linked Teams"}</label>
                            <Select
                                components={enableSelectAnimations ? animatedComponents : undefined}
                                options={teamOptions}
                                value={selectedTeamValue}
                                onChange={handleTeamChange}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                styles={colorStyles}
                                id={id ? `${id}-linked-teams-select` : "repo-linked-teams-select"}
                                isClearable
                            />
                            <p className="text-muted mt-2" style={{fontSize: '0.9em'}}>
                                You can add or remove linked teams to this repository. <span className={"!text-red-500"}>*</span>
                            </p>
                        </div>
                )}
                <div className="form-group mt-4" id={id ? `${id}-linked-secrets-group` : "repo-linked-secrets-group"}>
                    <label
                        id={id ? `${id}-linked-secrets-label` : "repo-linked-secrets-label"}>{creating ? "Link Secrets to Repository" : "Linked Secrets"}</label>
                    <Select
                        closeMenuOnSelect={false}
                        components={enableSelectAnimations ? animatedComponents : undefined}
                        isMulti
                        options={secretOptions}
                        value={selectedSecretValues}
                        onChange={handleSecretChange}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        styles={colorStyles}
                        id={id ? `${id}-linked-secrets-select` : "repo-linked-secrets-select"}
                    />
                    {!creating && (
                        <p className="text-muted mt-2" style={{fontSize: '0.9em'}}>
                            You can add or remove linked secrets to this repository.
                        </p>
                    )}
                </div>

            </div>
        </OdxModal>
    );
};

export default RepoSidesheet;
